// TypeScript mirrors of the backend owner_tools responses.
// Keep in sync with backend/app/services/owner_tools.py.

export interface OccupancyDay {
  date: string
  capacity: number
  occupied: number
  rate: number // 0..1
}

export interface OccupancyResult {
  start_date: string
  end_date: string
  capacity: number
  avg_rate: number
  empty_nights: number
  days: OccupancyDay[]
}

export interface RevenueLine {
  paid: number
  gross: number
  count: number
}

export interface RevenueResult {
  start_date: string
  end_date: string
  boarding: RevenueLine
  daycare: RevenueLine
  grooming: RevenueLine
  total_paid: number
  total_gross: number
}

export interface ChannelEntry {
  source: string
  count: number
  paid: number
}

export interface ChannelAttributionResult {
  start_date: string
  end_date: string
  channels: ChannelEntry[]
  total_count: number
}

export interface LostRevenueResult {
  start_date: string
  end_date: string
  capacity: number
  nights: number
  empty_nights: number
  avg_daily_rate: number
  lost_revenue: number
}

export interface RangeRequest {
  start_date?: string
  end_date?: string
}

export interface MarketingSegment {
  key: string
  label: string
  count: number
  hint?: string
  default_filters?: Record<string, unknown>
}
export interface MarketingSegmentsResult {
  segments: MarketingSegment[]
}

export interface SegmentPreviewResult {
  segment_type: string
  filters?: Record<string, unknown>
  count: number
  sample: { id: string; name: string }[]
}

export interface SendWinbackRequest {
  days_inactive?: number
  message_text: string
  name?: string
}
export interface SendWinbackResult {
  campaign_id: string
  segment_type: string
  filters?: Record<string, unknown>
  recipient_count: number
  sent: number
  failed: number
  status: string
}

export interface AssistantMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface AssistantChatRequest {
  message: string
  history?: AssistantMessage[]
}

export interface AssistantToolCall {
  name: string
  input: Record<string, unknown>
  output_preview: string
}

export interface AssistantChatResult {
  text: string
  tool_calls: AssistantToolCall[]
  finish_reason: string
  steps: number
}

// Phase 6A admin — public booking page state
export interface PublicBookingState {
  slug: string | null
  public_url: string | null
  enabled: boolean
  english_enabled: boolean
  display_name: string | null
  display_name_zh: string | null
  tagline: string | null
  tagline_zh: string | null
  // Phase 6A.5 identity pack
  logo_url: string | null
  accent_color: string | null
  cover_photos: string[]
  intro_video_url: string | null
  intro_video_provider: 'youtube' | 'vimeo' | null
  // SHARED-1 feature flags
  add_ons_enabled: boolean
  photo_cards_enabled: boolean
  tax_inclusive: boolean
  // E3 deposits. deposit_gateway_ready is read-only: whether an online rail
  // can actually collect (ECPay onboarded). If false, deposit_link is the
  // merchant's own fallback (LINE Pay / bank transfer).
  require_deposit: boolean
  deposit_percent: number
  deposit_link: string | null
  deposit_gateway_ready: boolean
}

export interface PublicBookingUpdate {
  slug?: string | null
  enabled?: boolean
  english_enabled?: boolean
  display_name?: string | null
  display_name_zh?: string | null
  tagline?: string | null
  tagline_zh?: string | null
  logo_url?: string | null
  accent_color?: string | null
  cover_photos?: string[]
  intro_video_url?: string | null
  add_ons_enabled?: boolean
  photo_cards_enabled?: boolean
  tax_inclusive?: boolean
  require_deposit?: boolean
  deposit_percent?: number
  deposit_link?: string | null
}

// Phase 6A.5 — R2 image upload
export interface PresignRequest {
  kind: 'logo' | 'gallery' | 'rich_menu'
  filename?: string
  content_type: string
  size_bytes?: number
}
export interface PresignResponse {
  upload_url: string
  public_url: string
  key: string
  expires_in: number
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}
